import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Bookmark, Star, Pencil, Trash2, Play, Plus, Save, X, ChevronDown, ChevronUp,
} from 'lucide-react';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from './ui/alert-dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import BetDateSelector from './BetDateSelector';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const summarizeBets = (bets) => {
    if (!bets?.length) return '';
    const parts = bets.slice(0, 3).map(b => {
        const d = (b.digits || []).join('-');
        if (b.game_type === 'Big 3') return `Big 3 ${d}`;
        const bet = b.bet_type === 'box' ? 'BX' : 'STR';
        return `${b.state} ${b.game_type === 'Pick 4' ? 'P4' : 'P3'} ${d} ${bet} $${Number(b.amount).toFixed(0)}`;
    });
    if (bets.length > 3) parts.push(`+${bets.length - 3} more`);
    return parts.join(' · ');
};

/**
 * Saved Slips library — name + persist multiple bet batches, replay any
 * of them with one tap.
 *
 * - Fetches /api/lottery/saved-slips on mount.
 * - "Replay" calls `onReplay(bets, label)` so the parent LotteryPhotoScan
 *   can open its review dialog seeded from this slip (via its ref).
 * - "Save current slip" is triggered externally by the parent via the
 *   `pendingSaveBets` prop — when set, the rename dialog auto-opens
 *   pre-filled with those bets so the player can name and save.
 */
const LotterySavedSlips = ({ onReplay, pendingSaveBets, onPendingSaveConsumed }) => {
    const [slips, setSlips] = useState([]);
    const [loading, setLoading] = useState(false);
    const [collapsed, setCollapsed] = useState(false);

    // Save / rename dialog state
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);     // { id, name, bets } for existing
    const [pendingBets, setPendingBets] = useState(null);  // bets to save as a new slip
    const [nameInput, setNameInput] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [pendingDelete, setPendingDelete] = useState(null);
    const [replayTarget, setReplayTarget] = useState(null);   // {slip, dates} during the date-prompt step
    const [replayDates, setReplayDates] = useState(null);

    const fetchSlips = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/lottery/saved-slips`);
            setSlips(data.rows || []);
        } catch {
            setSlips([]);
        }
        setLoading(false);
    }, []);

    useEffect(() => { fetchSlips(); }, [fetchSlips]);

    // When parent passes a fresh "just submitted" batch, auto-open the save dialog
    useEffect(() => {
        if (pendingSaveBets && pendingSaveBets.length > 0) {
            setPendingBets(pendingSaveBets);
            setEditing(null);
            setNameInput('');
            setDialogOpen(true);
            if (onPendingSaveConsumed) onPendingSaveConsumed();
        }
    }, [pendingSaveBets, onPendingSaveConsumed]);

    const openRename = (slip) => {
        setEditing(slip);
        setPendingBets(null);
        setNameInput(slip.name);
        setDialogOpen(true);
    };

    const closeDialog = () => {
        setDialogOpen(false);
        setEditing(null);
        setPendingBets(null);
        setNameInput('');
    };

    const handleSave = async () => {
        const name = nameInput.trim();
        if (!name) {
            toast.error('Give your slip a name.');
            return;
        }
        setSubmitting(true);
        try {
            if (editing) {
                await axios.patch(`${API}/lottery/saved-slips/${editing.id}`, { name });
                toast.success('Slip renamed');
            } else if (pendingBets) {
                // Strip volatile fields (id, selected, confidence) before saving
                const clean = pendingBets.map(b => ({
                    game_type: b.game_type,
                    digits: b.digits,
                    bet_type: b.bet_type,
                    amount: Number(b.amount) || 1,
                    state: b.state,
                    drawing_time: b.drawing_time,
                }));
                await axios.post(`${API}/lottery/saved-slips`, { name, bets: clean });
                toast.success(`Saved "${name}" to your library`);
            }
            await fetchSlips();
            closeDialog();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Could not save slip');
        }
        setSubmitting(false);
    };

    const handleDelete = async () => {
        const slip = pendingDelete;
        if (!slip) return;
        try {
            await axios.delete(`${API}/lottery/saved-slips/${slip.id}`);
            toast.success('Slip deleted');
            await fetchSlips();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Could not delete');
        }
        setPendingDelete(null);
    };

    // Step 1: open a small date prompt — older players often replay the
    // same number set for a different drawing, so we ask explicitly.
    const handleReplay = (slip) => {
        if (!onReplay) return;
        setReplayTarget(slip);
        setReplayDates(null);  // selector seeds to [today] on mount
    };

    // Step 2: user picked a date → apply it to every bet and pass through
    const confirmReplay = async () => {
        const slip = replayTarget;
        if (!slip) return;
        const dates = (replayDates && replayDates.length > 0) ? replayDates : undefined;
        const seeded = slip.bets.map(b => (dates ? { ...b, dates } : { ...b }));
        onReplay(seeded, slip.name);
        try { await axios.post(`${API}/lottery/saved-slips/${slip.id}/touch`); } catch { /* non-fatal */ }
        setReplayTarget(null);
        setReplayDates(null);
        setTimeout(() => { fetchSlips(); }, 800);
    };

    if (loading && slips.length === 0) {
        return null;
    }

    if (slips.length === 0) {
        // Show nothing if the player hasn't saved any yet — keeps the UI tidy.
        // The "Save this slip" button still gets surfaced via the parent's
        // pendingSaveBets flow when they finish a scan.
        return null;
    }

    return (
        <div className="mb-4" data-testid="lottery-saved-slips">
            <button
                type="button"
                onClick={() => setCollapsed(!collapsed)}
                className="w-full flex items-center justify-between mb-2 px-1 group"
                data-testid="lottery-saved-slips-toggle"
            >
                <span className="text-xs font-black uppercase tracking-widest text-[#39FF14] flex items-center gap-1">
                    <Bookmark className="w-3.5 h-3.5" />
                    Saved Slips · {slips.length}
                </span>
                <span className="text-[#A0AAB2] group-hover:text-white transition">
                    {collapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
                </span>
            </button>

            <AnimatePresence initial={false}>
                {!collapsed && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                    >
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {slips.map((s, idx) => (
                                <div
                                    key={s.id}
                                    className="rounded-lg border border-white/10 bg-[#0a0a0a] p-3 flex flex-col gap-2"
                                    data-testid={`lottery-saved-slip-${idx}`}
                                >
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="min-w-0 flex-1">
                                            <p className="text-white font-bold text-sm truncate flex items-center gap-1.5">
                                                <Star className="w-3.5 h-3.5 text-[#FFD700] shrink-0" />
                                                {s.name}
                                            </p>
                                            <p className="text-[10px] text-[#A0AAB2] mt-0.5 line-clamp-2 break-words">
                                                {summarizeBets(s.bets)}
                                            </p>
                                            {s.use_count > 0 && (
                                                <p className="text-[9px] text-[#666] mt-0.5">
                                                    Played {s.use_count} time{s.use_count === 1 ? '' : 's'}
                                                </p>
                                            )}
                                        </div>
                                        <div className="flex gap-1 shrink-0">
                                            <button
                                                type="button"
                                                onClick={() => openRename(s)}
                                                className="p-1.5 text-[#A0AAB2] hover:text-white hover:bg-white/5 rounded"
                                                aria-label="Rename"
                                                data-testid={`lottery-saved-slip-rename-${idx}`}
                                            >
                                                <Pencil className="w-3.5 h-3.5" />
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setPendingDelete(s)}
                                                className="p-1.5 text-[#FF3B30] hover:bg-[#FF3B30]/10 rounded"
                                                aria-label="Delete"
                                                data-testid={`lottery-saved-slip-delete-${idx}`}
                                            >
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    </div>
                                    <Button
                                        size="sm"
                                        onClick={() => handleReplay(s)}
                                        className="bg-[#39FF14]/15 hover:bg-[#39FF14]/25 text-[#39FF14] border border-[#39FF14]/40 h-8 text-xs font-bold"
                                        data-testid={`lottery-saved-slip-replay-${idx}`}
                                    >
                                        <Play className="w-3 h-3 mr-1" />
                                        Replay · {s.bets.length} bet{s.bets.length === 1 ? '' : 's'}
                                    </Button>
                                </div>
                            ))}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Save / Rename dialog */}
            <Dialog open={dialogOpen} onOpenChange={(o) => { if (!o) closeDialog(); }}>
                <DialogContent className="bg-[#0e0e0e] border-white/10 text-white max-w-md" data-testid="lottery-saved-slips-dialog">
                    <DialogHeader>
                        <DialogTitle className="text-white text-lg font-black flex items-center gap-2">
                            {editing ? <Pencil className="w-4 h-4 text-[#FFD700]" /> : <Save className="w-4 h-4 text-[#39FF14]" />}
                            {editing ? 'Rename Slip' : 'Save to Library'}
                        </DialogTitle>
                        <DialogDescription className="text-[#A0AAB2]">
                            {editing
                                ? 'Give your slip a new name.'
                                : 'Name this batch so you can replay it with one tap from your library.'}
                        </DialogDescription>
                    </DialogHeader>

                    <div>
                        <Label className="text-[11px] text-[#A0AAB2] mb-1 block">Name</Label>
                        <Input
                            value={nameInput}
                            onChange={(e) => setNameInput(e.target.value)}
                            placeholder={'e.g. "Mom\'s birthday picks"'}
                            maxLength={60}
                            className="h-10 bg-[#0a0a0a] border-white/10 text-white"
                            data-testid="lottery-saved-slips-name-input"
                            autoFocus
                        />
                        {pendingBets && pendingBets.length > 0 && (
                            <p className="text-[10px] text-[#A0AAB2] mt-2">
                                Saving {pendingBets.length} bet{pendingBets.length === 1 ? '' : 's'} · {summarizeBets(pendingBets)}
                            </p>
                        )}
                    </div>

                    <DialogFooter className="flex-row gap-2">
                        <Button
                            variant="outline"
                            onClick={closeDialog}
                            className="flex-1 border-white/15 text-white hover:bg-white/5"
                            data-testid="lottery-saved-slips-cancel-btn"
                        >
                            <X className="w-4 h-4 mr-1" />
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSave}
                            disabled={submitting || !nameInput.trim()}
                            className="flex-1 bg-[#39FF14] hover:bg-[#39FF14]/90 text-black font-black"
                            data-testid="lottery-saved-slips-save-btn"
                        >
                            <Save className="w-4 h-4 mr-1" />
                            {submitting ? 'Saving…' : (editing ? 'Save name' : 'Save slip')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete confirmation */}
            {/* Replay date prompt — pick which drawing(s) to play before re-firing */}
            <Dialog open={!!replayTarget} onOpenChange={(o) => { if (!o) { setReplayTarget(null); setReplayDates(null); } }}>
                <DialogContent className="bg-[#0e0e0e] border-white/10 text-white max-w-md" data-testid="lottery-saved-slip-replay-dialog">
                    <DialogHeader>
                        <DialogTitle className="text-white flex items-center gap-2">
                            <Play className="w-5 h-5 text-[#39FF14]" />
                            Replay &quot;{replayTarget?.name}&quot;
                        </DialogTitle>
                        <DialogDescription className="text-[#A0AAB2]">
                            Pick which drawing(s) to play these {replayTarget?.bets?.length || 0} bets for. The next screen lets you tweak individual rows before locking them in.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="rounded-lg border border-[#7C3AED]/30 bg-[#7C3AED]/5 p-3">
                        <BetDateSelector
                            dates={replayDates}
                            onChange={setReplayDates}
                            testIdPrefix="lottery-saved-slip-replay-date"
                        />
                    </div>
                    <DialogFooter className="flex-row gap-2">
                        <Button
                            variant="outline"
                            onClick={() => { setReplayTarget(null); setReplayDates(null); }}
                            className="flex-1 border-white/15 text-white hover:bg-white/5"
                            data-testid="lottery-saved-slip-replay-cancel"
                        >
                            <X className="w-4 h-4 mr-1" />
                            Cancel
                        </Button>
                        <Button
                            onClick={confirmReplay}
                            className="flex-1 bg-[#39FF14] text-black hover:bg-[#39FF14]/90 font-black"
                            data-testid="lottery-saved-slip-replay-continue"
                        >
                            <Play className="w-4 h-4 mr-1" />
                            Continue
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <AlertDialog open={!!pendingDelete} onOpenChange={(o) => { if (!o) setPendingDelete(null); }}>
                <AlertDialogContent className="bg-[#0e0e0e] border-white/10 text-white" data-testid="lottery-saved-slip-delete-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="text-white">Delete saved slip?</AlertDialogTitle>
                        <AlertDialogDescription className="text-[#A0AAB2]">
                            &quot;{pendingDelete?.name}&quot; will be removed from your library. This can&apos;t be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="bg-transparent border-white/15 text-white hover:bg-white/5" data-testid="lottery-saved-slip-delete-cancel-btn">Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDelete} className="bg-[#FF3B30] hover:bg-[#FF3B30]/90 text-white" data-testid="lottery-saved-slip-delete-confirm-btn">
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};

export default LotterySavedSlips;
